import {
  buffers, type Channel,
} from 'redux-saga';
import {
  actionChannel, call, put, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import {
  type ApiError, isApiConcurrencyError, isApiError, isNotFoundApiError,
} from '~/_shared/utils/api/apiError.helpers';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import { copy } from '~/_shared/utils/collections/collections';
import { createUuid } from '~/_shared/utils/createUuid';
import { refreshPage } from '~/_shared/utils/document/document.helpers';
import { createDefaultDemographicsMetrics } from '~/_shared/utils/metric/defaultMetric.factory';
import { getDefaultSpreadsheetColumnMetrics } from '~/_shared/utils/metric/defaultMetric.sagas';
import {
  mapMetricsToServerModel, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import { select } from '~/_shared/utils/saga/effects';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { defaultBoundaryTerritoryGroupResponseSettings } from '~/boundary/settings/defaultBoundarySettings';
import { boundaryTerritoryGroupCreateSettingsStyleDefaults } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { getCalculateBucketFunction } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import { applySharedOpacity } from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSettings.helpers';
import { appStore } from '~/store/app.store';
import {
  type BoundaryTerritoryGroupId, type ExtendedBoundaryIdentifier,
} from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryGroupsRequest } from '~/store/boundaryGroups/boundaryGroups.actionCreators';
import { cloneBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.repository';
import { activeMapElementsClearState } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { activeBoundarySelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import { frontedStateProcessingBoundariesInitialized } from '~/store/frontendState/processing/processing.actionCreators';
import { isMapInOfflineModeSelector } from '~/store/selectors/useMapInfoSelectors';
import { translate } from '../../translations/Trans';
import { type BoundaryGroupsAction } from '../boundaryGroups/boundaryGroups.actions';
import {
  BOUNDARY_GROUP_CREATE_SUCCESS, BOUNDARY_GROUP_FETCH_ALL_ERROR, BOUNDARY_GROUP_FETCH_ALL_SUCCESS,
} from '../boundaryGroups/boundaryGroups.actionTypes';
import {
  boundaryGroupIsCustomSelector, boundaryGroupsSelector, getBoundaryGroupSelector,
} from '../boundaryGroups/boundaryGroups.selectors';
import { type BoundaryItemsAction } from '../boundaryItems/boundaryItems.action';
import { BOUNDARY_ITEMS_REMOVE_ITEM_RESPONSE_RECEIVED } from '../boundaryItems/boundaryItems.actionTypes';
import { getBoundaryTerritories } from '../boundaryTerritoryDetails/boundaryTerritoryDetails.repository';
import { type MapIdAction } from '../mapId/mapId.action';
import { MAP_ID_SET } from '../mapId/mapId.actionTypes';
import { primarySpreadsheetIdSelector } from '../mapInfo/mapInfo.selectors';
import {
  closeModal, createAppError, openNewModalWithId,
} from '../modal/modal.actionCreators';
import { rowRelationsEventReceived } from '../rowRelations/rowRelations.actionCreators';
import { clientIdSelector } from '../selectors/useClientIdSelector';
import { mapIdSelector } from '../selectors/useMapIdSelector';
import { type FilterTreeItemRequest } from '../spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { getStoreFilterTree } from '../spreadsheetData/filterTree/spreadsheetDataFilterTree.helpers';
import type { spreadsheetResetStateAndRefetchData } from '../spreadsheetData/spreadsheetData.actionCreators';
import { SPREADSHEET_RESET_STATE_AND_REFETCH_DATA } from '../spreadsheetData/spreadsheetData.actionTypes';
import { BoundaryTerritoryType } from './boundaryTerritoryGroup.type';
import { type BoundaryTerritoryGroupsAction } from './boundaryTerritoryGroups.action';
import {
  boundaryTerritoryGroupActivate, boundaryTerritoryGroupsBulkSaveRequest, boundaryTerritoryGroupsCreateCancel,
  boundaryTerritoryGroupsCreateError, boundaryTerritoryGroupsCreateRequest, boundaryTerritoryGroupsCreateSuccess,
  boundaryTerritoryGroupsFetchError, boundaryTerritoryGroupsFetchRequest, boundaryTerritoryGroupsFetchSuccess,
  boundaryTerritoryGroupsRemoveCompleted, boundaryTerritoryGroupsRemoveError, boundaryTerritoryGroupsUpdateCancel,
  boundaryTerritoryGroupsUpdateError, boundaryTerritoryGroupsUpdateRequest,
  boundaryTerritoryGroupUpdateAssignmentsError, boundaryTerritoryGroupUpdateAssignmentsSuccess,
  boundaryTerritoryGroupUpdateSuccess,
} from './boundaryTerritoryGroups.actionCreators';
import {
  BOUNDARY_TERRITORY_GROUP_CLONE_REQUEST, BOUNDARY_TERRITORY_GROUP_CREATE_REQUEST,
  BOUNDARY_TERRITORY_GROUP_FETCH_REQUEST, BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
  BOUNDARY_TERRITORY_GROUP_PATCH_SETTINGS, BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED,
  BOUNDARY_TERRITORY_GROUP_REMOVE_REQUEST, BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_REQUEST,
  BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST,
} from './boundaryTerritoryGroups.actionTypes';
import {
  convertBTGMatchupRequestToResponse, convertBTGSettingsRequestToResponse,
  createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup, createTerritoryBoundaryGroupFromResponse,
  createTerritoryBoundaryGroupModelFromResponse,
} from './boundaryTerritoryGroups.factory';
import { isArtificialBoundaryTerritoryGroup } from './boundaryTerritoryGroups.helpers';
import {
  BoundaryMatchingType, type BoundaryTerritoryGroupCreateRequest, type BoundaryTerritoryGroupModelResponse,
  type BoundaryTerritoryGroupResponse, type BoundaryTerritoryGroupsResponse, bulkUpdateBoundaryTerritoryGroups,
  createBoundaryTerritoryGroup, getBoundaryTerritoryGroups, removeBoundaryTerritoryGroup,
  updateBoundaryTerritoryAssignments, updateBoundaryTerritoryGroup,
} from './boundaryTerritoryGroups.repository';
import {
  boundaryTerritoryGroupsSelector, findBoundaryTerritoryGroupSelector, type GetBTGById,
} from './boundaryTerritoryGroups.selectors';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupModel,
} from './boundaryTerritoryGroups.state';
import { boundaryToolBTGsSelector } from './boundaryTool/boundaryToolBTGs.selectors';

export const BOUNDARY_TERRITORIES_MAYBE_REMOVED_ACTIONS = [
  BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS,
  BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED,
];

export function* boundaryTerritoryGroupsSagas() {
  yield takeLatest(MAP_ID_SET, onMapIdChange);
  yield takeLatest(SPREADSHEET_RESET_STATE_AND_REFETCH_DATA, onSpreadsheetDataChange);
  yield takeLatest(rowRelationsEventReceived, onRowRelationsChange);
  yield takeLatest(BOUNDARY_TERRITORY_GROUP_FETCH_REQUEST, onFetchBoundaryTerritoryGroupsRequest);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_CREATE_REQUEST, boundaryTerritoryGroupCreateRequest);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_CLONE_REQUEST, cloneBoundaryTerritoryGroup);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_REMOVE_REQUEST, removeTerritoryGroup);
  yield takeEvery(BOUNDARY_GROUP_CREATE_SUCCESS, onBoundaryGroupCreateSuccess);
  yield takeLatest(BOUNDARY_GROUP_FETCH_ALL_SUCCESS, onBoundaryGroupsFetchAllSuccess);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST, onUpdateBoundaryTerritoryGroup);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_REQUEST, updateTerritoryGroupAssignments);
  yield takeEvery(BOUNDARY_ITEMS_REMOVE_ITEM_RESPONSE_RECEIVED, removeOutdatedBoundaryStyleAssignments);
  yield takeEvery(BOUNDARY_TERRITORY_GROUP_PATCH_SETTINGS, updateBoundaryTerritoryGroupSettingsField);
  yield takeEvery(BOUNDARY_TERRITORIES_MAYBE_REMOVED_ACTIONS, ensureRemovedBTGConsistency);
  yield takeEvery(boundaryTerritoryGroupsBulkSaveRequest, onBoundaryTerritoryGroupsBulkSave);
}

function* onBoundaryGroupCreateSuccess(action: PickAction<BoundaryGroupsAction, typeof BOUNDARY_GROUP_CREATE_SUCCESS>) {
  const mapId: number | null = yield select(mapIdSelector);

  if (mapId === null) {
    return;
  }

  yield put(boundaryTerritoryGroupsCreateRequest(
    {
      map_id: mapId,
      boundary_group_id: action.payload.boundaryGroup.id,
      matchings: {
        matching_type: BoundaryMatchingType.Geometry,
      },
      settings: {
        ...defaultBoundaryTerritoryGroupResponseSettings,
        boundary_territories: [],
        boundary_territory_type: BoundaryTerritoryType.Manual,
        style: boundaryTerritoryGroupCreateSettingsStyleDefaults,
        calculate_bucket_function: getCalculateBucketFunction(BoundaryTerritoryType.Manual),
      },
    },
    action.payload.onBoundaryTerritoryGroupCreateSuccess,
  ));
}

// If BTG got created before the boundary groups were fetched the default BTG settings allow labels to be shown, this ensures consistency with the boundary group's label settings
function* onBoundaryGroupsFetchAllSuccess(
  action: PickAction<BoundaryGroupsAction, typeof BOUNDARY_GROUP_FETCH_ALL_SUCCESS>
) {
  const { boundaryGroups } = action.payload;
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);

  const boundaryTerritoryGroupsToHideLabels = boundaryTerritoryGroups.reduce<BoundaryTerritoryGroup[]>((acc, btg) => {
    const boundaryGroup = boundaryGroups.find(group => group.id === btg.boundaryGroupId);
    const labelsAreShown = !btg.settings.hideLabels;

    if (boundaryGroup && boundaryGroup.hideLabels && labelsAreShown) {
      acc.push({
        ...btg,
        settings: {
          ...btg.settings,
          hideLabels: true,
        },
      });
    }

    return acc;
  }, []);

  if (boundaryTerritoryGroupsToHideLabels.length) {
    yield put(boundaryTerritoryGroupUpdateSuccess(boundaryTerritoryGroupsToHideLabels, { fetchBoundaryDetails: false }));
  }
}

function* onMapIdChange(action: PickAction<MapIdAction, typeof MAP_ID_SET>) {
  if (action.payload.mapId === null) {
    return;
  }

  yield put(boundaryTerritoryGroupsFetchRequest(action.payload.mapId));
}

function* onSpreadsheetDataChange(action: ReturnType<typeof spreadsheetResetStateAndRefetchData>) {
  if (!action.payload.reloadBTGs) {
    return;
  }

  yield call(requestBoundaryTerritoryGroupsRefresh);
}

function* onRowRelationsChange(action: ReturnType<typeof rowRelationsEventReceived>) {
  if (action.payload.event.status !== 'FINISHED') {
    return;
  }

  if (action.payload.event.reason !== 'SPREADSHEET_UPDATE') {
    return;
  }

  const spreadsheetId: number | null = yield select<number | null>(primarySpreadsheetIdSelector);

  if (spreadsheetId !== action.payload.event.virtualSpreadsheetId) {
    return;
  }

  yield call(requestBoundaryTerritoryGroupsRefresh);
}

function* requestBoundaryTerritoryGroupsRefresh() {
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);
  const mapId: number | null = yield select<number | null>(mapIdSelector);

  if (isMapOffline || mapId === null) {
    return;
  }

  yield put(boundaryTerritoryGroupsFetchRequest(mapId));
}

function* fetchBoundaryTerritoryGroups(clientId: number, mapId: number) {
  try {
    const response: BoundaryTerritoryGroupsResponse = yield call(getBoundaryTerritoryGroups, clientId, {
      map_id: mapId,
    });

    yield put(boundaryTerritoryGroupsFetchSuccess(response.boundary_territory_groups.map(item =>
      createTerritoryBoundaryGroupFromResponse(item)
    )));

    if (!response.boundary_territory_groups.length) {
      yield put(frontedStateProcessingBoundariesInitialized());
    }
  }
  catch (e) {
    yield put(boundaryTerritoryGroupsFetchError(e));
  }
}

function* onFetchBoundaryTerritoryGroupsRequest(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_FETCH_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);

  if (clientId === null) {
    return;
  }

  yield fetchBoundaryTerritoryGroups(clientId, action.payload.mapId);
}

function* boundaryTerritoryGroupCreateRequest(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_CREATE_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);

  if (!clientId) {
    return;
  }

  if (isMapOffline) {
    const newBoundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield call(
      createOfflineBoundaryTerritoryGroup,
      [action.payload.request],
      clientId,
    );

    if (newBoundaryTerritoryGroups && newBoundaryTerritoryGroups[0]) {
      action.payload.onSuccess?.(newBoundaryTerritoryGroups[0]);
    }
    return;
  }

  try {
    const requestData = action.payload.request;
    const allBoundaryGroups: BoundaryGroup[] = yield select(boundaryGroupsSelector);
    const boundaryGroup = allBoundaryGroups.find(bg => bg.id === requestData.boundary_group_id);
    const defaultDemographicsMetrics = boundaryGroup ? createDefaultDemographicsMetrics(allBoundaryGroups, boundaryGroup.id) : [];
    const defaultSpreadsheetColumnMetrics: MetricModel[] = yield call(getDefaultSpreadsheetColumnMetrics);

    const serverModelMetrics = mapMetricsToServerModel([
      ...defaultDemographicsMetrics,
      ...defaultSpreadsheetColumnMetrics,
    ]);

    const requestDataWithDefaultMetric: BoundaryTerritoryGroupCreateRequest = {
      ...requestData,
      settings: {
        ...requestData.settings,
        metrics: requestData.settings.metrics ?? serverModelMetrics,
        hide_labels: boundaryGroup?.hideLabels || requestData.settings.hide_labels,
      },
    };

    const response: BoundaryTerritoryGroupResponse = yield call(
      createBoundaryTerritoryGroup,
      clientId,
      requestDataWithDefaultMetric
    );

    const newBoundaryTerritoryGroup = createTerritoryBoundaryGroupFromResponse(response);

    yield put(boundaryTerritoryGroupsCreateSuccess([newBoundaryTerritoryGroup]));
    action.payload.onSuccess?.(newBoundaryTerritoryGroup);
  }
  catch (e) {
    yield call(onBoundaryTerritoryGroupCreateError, e, action.payload.request, action.payload.onSuccess);
  }
}

function* cloneBoundaryTerritoryGroup(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_CLONE_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);

  if (!clientId) {
    action.payload.onError?.(new Error('clientId is null'));
    return;
  }

  try {
    yield call(
      cloneBoundaryGroup,
      clientId,
      action.payload.request.boundary_territory_group_id,
      action.payload.request.new_boundary_territory_group_name
    );

    const refreshChannel: Channel<Action> = yield actionChannel([BOUNDARY_GROUP_FETCH_ALL_SUCCESS, BOUNDARY_GROUP_FETCH_ALL_ERROR], buffers.sliding(1));
    yield put(boundaryGroupsRequest());
    yield take(refreshChannel);

    action.payload.onSuccess?.();
  }
  catch (e) {
    action.payload.onError?.(e);
  }
}

type BoundaryTerritoryCreateUpdateApiError = ApiError & {
  readonly response: {
    readonly btg: BoundaryTerritoryGroupId;
  };
};

const isBoundaryTerritoryCreateUpdateApiError = (error: any): error is BoundaryTerritoryCreateUpdateApiError => {
  return error && (typeof error === 'object') && ('response' in error) && ('btg' in error.response) && (error.errorCode === 'BTG_FILL_NOT_UNIQUE') &&
    isApiError(error) && (error.responseStatus >= 400) && (error.responseStatus < 500);
};

function* onBoundaryTerritoryGroupCreateError(
  error: any,
  request: BoundaryTerritoryGroupCreateRequest,
  onDone?: (boundaryTerritoryGroup: BoundaryTerritoryGroup) => void,
) {
  const clientId: number | null = yield select(clientIdSelector);
  // Fill was potentially already added to the map
  if (isBoundaryTerritoryCreateUpdateApiError(error) && (clientId !== null)) {
    const boundaryTerritoryGroups: ReturnType<typeof boundaryTerritoryGroupsSelector> =
      yield select<ReturnType<typeof boundaryTerritoryGroupsSelector>>(boundaryTerritoryGroupsSelector);
    const btgToSelect = boundaryTerritoryGroups.find(btg => btg.boundaryTerritoryGroupId === error.response.btg);

    if (btgToSelect) {
      onDone?.(btgToSelect);
      yield put(boundaryTerritoryGroupsCreateCancel());
      yield put(boundaryTerritoryGroupHighlightStarted(btgToSelect.boundaryGroupId));
      yield put(boundaryTerritoryGroupActivate(btgToSelect));

      return;
    }
    else {
      yield call(openConcurrencyErrorModal, {
        boundaryGroupId: request.boundary_group_id,
      });
    }
  }
  yield put(boundaryTerritoryGroupsCreateError());
  yield put(createAppError({
    type: AppErrorType.General,
    errorTitle: translate('An error occurred while creating the boundary'),
    title: translate('Boundary Create Error'),
  }));

  console.error(error);
}

let temporaryBoundaryTerritoryGroupIdCounter: number = -1;

export function* createOfflineBoundaryTerritoryGroup(
  requests: ReadonlyArray<BoundaryTerritoryGroupCreateRequest & {
    boundaryTerritoryGroupId?: BoundaryTerritoryGroupId;
    sharedOpacity?: number;
  }>,
  clientId: number,
) {
  const { getBoundaryGroupById }: ReturnType<typeof getBoundaryGroupSelector> = yield select(getBoundaryGroupSelector);
  try {
    const requestEntries = requests.map(request => {
      const boundaryGroup = getBoundaryGroupById(request.boundary_group_id);
      const btgSettingsPatch = boundaryGroup?.hideLabels ? { hide_labels: true } : {};
      const requestId = createUuid();
      const itemResponse: BoundaryTerritoryGroupModelResponse = {
        map_id: request.map_id,
        boundary_group_id: request.boundary_group_id,
        boundary_territory_group_id: request.boundaryTerritoryGroupId,
        archived_at: null,
        matchings: convertBTGMatchupRequestToResponse(request.matchings),
        settings: convertBTGSettingsRequestToResponse({ ...request.settings, ...btgSettingsPatch }, request.settings.boundary_territories ?? []),
      };
      const newTerritoryGroup: BoundaryTerritoryGroupModel = createTerritoryBoundaryGroupModelFromResponse(itemResponse);
      return { requestId, newTerritoryGroup, sharedOpacity: request.sharedOpacity };
    });

    const generatedBoundaryTerritories: Awaited<ReturnType<typeof getBoundaryTerritories>> = yield call(
      getBoundaryTerritories,
      clientId,
      requestEntries.map(({ requestId, newTerritoryGroup }) => ({
        boundaryTerritoryGroup: newTerritoryGroup,
        boundaryTerritoryGroupQueryId: requestId,
        filter: null,
      }))
    );

    const newBoundaryTerritoryGroups: BoundaryTerritoryGroup[] = [];
    for (const { requestId, newTerritoryGroup, sharedOpacity } of requestEntries) {
      const generated = generatedBoundaryTerritories.get(requestId);
      if (!generated) {
        continue;
      }

      const newBoundaryTerritoryGroupId = generated.boundaryTerritoryGroupId ?? temporaryBoundaryTerritoryGroupIdCounter--;
      const shouldUseGeneratedBTs = newTerritoryGroup.settings.boundaryTerritories?.every(bt => bt.custom) ?? true; // empty or all custom

      const generatedBTsWithOpacity = notNullsy(sharedOpacity)
        ? applySharedOpacity(generated.boundaryTerritories, sharedOpacity)
        : generated.boundaryTerritories;

      newBoundaryTerritoryGroups.push({
        ...newTerritoryGroup,
        boundaryTerritoryGroupId: newBoundaryTerritoryGroupId,
        settings: {
          ...newTerritoryGroup.settings,
          boundaryTerritories: shouldUseGeneratedBTs
            ? generatedBTsWithOpacity
            : newTerritoryGroup.settings.boundaryTerritories,
        },
      });
    }

    if (newBoundaryTerritoryGroups.length === 0) {
      yield put(boundaryTerritoryGroupsCreateCancel());
      return;
    }

    yield put(boundaryTerritoryGroupsCreateSuccess(newBoundaryTerritoryGroups));

    return newBoundaryTerritoryGroups;
  }
  catch (e) {
    yield put(boundaryTerritoryGroupsCreateError());
    yield put(createAppError({
      type: AppErrorType.General,
      errorTitle: translate('An error occurred while creating the boundary'),
      title: translate('Boundary Create Error'),
    }));
  }

  return null;
}

function* onUpdateBoundaryTerritoryGroup(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_UPDATE_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);
  const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);
  const previousBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(action.payload.updatedBoundaryTerritoryGroup.boundaryTerritoryGroupId);

  if (!clientId || !previousBoundaryTerritoryGroup) {
    return;
  }

  const updatedBoundaryTerritoryGroup = action.payload.updatedBoundaryTerritoryGroup;

  if (isMapOffline || isArtificialBoundaryTerritoryGroup(updatedBoundaryTerritoryGroup)) {
    yield call(
      updateOfflineBoundaryTerritoryGroups,
      [updatedBoundaryTerritoryGroup],
      action.payload,
      clientId,
    );
  }
  else {
    const updateRequest = createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup(updatedBoundaryTerritoryGroup);

    try {
      const newTerritoryGroup: BoundaryTerritoryGroup = yield call(
        updateBoundaryTerritoryGroup,
        clientId,
        updateRequest
      );

      action.payload.onDone?.();

      yield put(boundaryTerritoryGroupUpdateSuccess([newTerritoryGroup], { fetchBoundaryDetails: action.payload.fetchBoundaryDetails }));
    }
    catch (e) {
      yield call(onBoundaryTerritoryGroupUpdateError, {
        error: e,
        boundaryTerritoryGroup: action.payload.updatedBoundaryTerritoryGroup,
        onSuccess: action.payload.onDone,
        failSilently: action.payload.failSilently,
        refetchOnConcurrencyError: action.payload.refetchOnConcurrencyError,
      });
    }
  }
}

export function* updateOfflineBoundaryTerritoryGroups(
  boundaryTerritoryGroups: NonEmptyArray<BoundaryTerritoryGroup>,
  { fetchBoundaryDetails, failSilently, onDone }: Readonly<{
    fetchBoundaryDetails: boolean;
    failSilently: boolean;
    onDone?: () => void;
  }>,
  clientId: number
) {
  try {
    if (!fetchBoundaryDetails) {
      yield put(boundaryTerritoryGroupUpdateSuccess(boundaryTerritoryGroups, { fetchBoundaryDetails: false }));
      return;
    }

    const combinedFilterTrees: FilterTreeItemRequest | null = yield getStoreFilterTree();

    const detailsRequests = boundaryTerritoryGroups.map(boundaryTerritoryGroup => {
      return {
        boundaryTerritoryGroup,
        boundaryTerritoryGroupQueryId: createUuid(),
        filter: combinedFilterTrees?.filterTree ? {
          filter_tree: combinedFilterTrees.filterTree,
        } : null,
      };
    });

    const detailResponseMap: Awaited<ReturnType<typeof getBoundaryTerritories>> = yield call(
      getBoundaryTerritories,
      clientId,
      detailsRequests,
    );

    const details = [...detailResponseMap.values()];
    const detailsBtgQueryIds = details.map(btgDetails => btgDetails.boundaryTerritoryGroupQueryId);

    if (detailsRequests.some(btg => !detailsBtgQueryIds.includes(btg.boundaryTerritoryGroupQueryId))) {
      yield put(boundaryTerritoryGroupsUpdateCancel());
      return;
    }

    yield put(boundaryTerritoryGroupUpdateSuccess(detailsRequests.map(detailsRequest => {
      const generated = details.find(d => d.boundaryTerritoryGroupQueryId === detailsRequest.boundaryTerritoryGroupQueryId);

      if (!generated) {
        return detailsRequest.boundaryTerritoryGroup;
      }

      const originalBoundaryTerritories = detailsRequest.boundaryTerritoryGroup.settings.boundaryTerritories;
      const shouldUseGeneratedBTs = originalBoundaryTerritories.every(bt => bt.custom) ?? true; // empty or all custom

      return {
        ...detailsRequest.boundaryTerritoryGroup,
        settings: {
          ...detailsRequest.boundaryTerritoryGroup.settings,
          boundaryTerritories: shouldUseGeneratedBTs ? generated.boundaryTerritories : originalBoundaryTerritories,
        },
      };
    }, { fetchBoundaryDetails })));

    onDone?.();
  }
  catch (e) {
    yield put(boundaryTerritoryGroupsUpdateError());

    if (failSilently) {
      return;
    }
    else {
      yield put(createAppError({
        type: AppErrorType.General,
        errorTitle: translate('An error occurred while updating the data'),
        title: translate('Boundary Update Error'),
      }));
    }
  }
}

function* updateTerritoryGroupAssignments(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_UPDATE_ASSIGNMENTS_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);

  if (!clientId) {
    return;
  }

  try {
    if (!isMapOffline) {
      yield call(updateBoundaryTerritoryAssignments, clientId, action.payload.boundaryTerritoryGroupId, action.payload.requests);
    }

    yield put(boundaryTerritoryGroupUpdateAssignmentsSuccess(action.payload.boundaryTerritoryGroupId, action.payload.requests));
    action.payload.onSuccess?.();
  }
  catch (e) {
    yield put(boundaryTerritoryGroupUpdateAssignmentsError());
  }
}

function* removeTerritoryGroup(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_REMOVE_REQUEST>
) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);
  const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);
  const BTGsToRemove = action.payload.boundaryTerritoryGroupIds
    .map(getBoundaryTerritoryGroupById)
    .filter(notNullsy);

  if (!clientId) {
    return;
  }
  if (isMapOffline) {
    yield put(boundaryTerritoryGroupsRemoveCompleted(BTGsToRemove));
    return;
  }
  const removedBTGs: typeof BTGsToRemove = [];
  const errorBTGs: Readonly<{ error: any; boundaryTerritoryGroup: typeof BTGsToRemove[0] }>[] = [];

  for (const boundaryTerritoryGroup of BTGsToRemove) {
    try {
      yield call(removeBoundaryTerritoryGroup, clientId, boundaryTerritoryGroup.boundaryTerritoryGroupId);

      removedBTGs.push(boundaryTerritoryGroup);
    }
    catch (e) {
      errorBTGs.push({
        boundaryTerritoryGroup,
        error: e,
      });
    }
  }
  if (removedBTGs.length > 0) {
    yield put(boundaryTerritoryGroupsRemoveCompleted(removedBTGs));
  }
  if (errorBTGs.length > 0) {
    for (const { boundaryTerritoryGroup, error } of errorBTGs) {
      yield call(onRemoveTerritoryGroupError, error, boundaryTerritoryGroup);
    }
  }
}

function* handleTerritoryGroupAlreadyRemoved(e: any, removedBoundaryTerritoryGroup: BoundaryTerritoryGroup) {
  // potentially the boundary territory group is already removed - refetch BTGs and compare with removed BTG
  if (isNotFoundApiError(e)) {
    const clientId: number | null = yield select<number | null>(clientIdSelector);
    const mapId: number | null = yield select<number | null>(mapIdSelector);

    if (clientId === null || mapId === null) {
      return;
    }

    yield fetchBoundaryTerritoryGroups(clientId, mapId);
    const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);

    const isGroupStillExisting = !!getBoundaryTerritoryGroupById(removedBoundaryTerritoryGroup.boundaryTerritoryGroupId);
    if (!isGroupStillExisting) {
      return true;
    }
  }

  return false;
}

function* onRemoveTerritoryGroupError(error: any, removedBoundaryTerritoryGroup: BoundaryTerritoryGroup) {
  const isGroupCustom: ReturnType<typeof boundaryGroupIsCustomSelector> = yield select(boundaryGroupIsCustomSelector, removedBoundaryTerritoryGroup.boundaryGroupId);

  const isBoundaryTerritoryGroupRemoved: boolean = yield call(
    handleTerritoryGroupAlreadyRemoved,
    error, removedBoundaryTerritoryGroup);
  if (isBoundaryTerritoryGroupRemoved) {
    yield put(boundaryTerritoryGroupsRemoveCompleted([removedBoundaryTerritoryGroup]));
    return;
  }

  yield put(boundaryTerritoryGroupsRemoveError());
  yield put(createAppError({
    type: AppErrorType.General,
    title: translate(isGroupCustom ? 'Territory Group Remove Error' : 'Boundary Remove Error'),
    errorTitle: translate('An error occurred while removing the data'),
  }));
}

// It's nice to have, but not necessary. This will remove styles from B-T-Gs on current map, other maps are unnafected.
// If the function causes problems, it can be deleted. Outdated styles do not cause any problems.
function* removeOutdatedBoundaryStyleAssignments(action: PickAction<BoundaryItemsAction, typeof BOUNDARY_ITEMS_REMOVE_ITEM_RESPONSE_RECEIVED>) {
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(
    boundaryToolBTGsSelector
  );

  for (const boundaryTerritoryGroup of boundaryTerritoryGroups) {
    if (!boundaryTerritoryGroup.settings.boundaryStyle.has(action.payload.boundaryId)) {
      continue;
    }

    // This consistency update and the whole saga are optional.
    // We don't want to stress the user when an update fails due to concurrency (etag) or other reasons.
    const failSilently = true;

    yield put(boundaryTerritoryGroupsUpdateRequest({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryStyle: copy.andRemove(boundaryTerritoryGroup.settings.boundaryStyle, [action.payload.boundaryId]),
      },
    }, { failSilently }));
  }
}

function* updateBoundaryTerritoryGroupSettingsField(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof BOUNDARY_TERRITORY_GROUP_PATCH_SETTINGS>
) {
  const { boundaryTerritoryGroupId, patcher } = action.payload;
  const getBoundaryTerritoryGroupById: GetBTGById = yield select<GetBTGById>(findBoundaryTerritoryGroupSelector);
  const btgToUpdate = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);
  if (!btgToUpdate) {
    return;
  }

  const newSettings = patcher({ ...btgToUpdate.settings });

  yield put(boundaryTerritoryGroupsUpdateRequest({
    ...btgToUpdate,
    settings: newSettings,
  }));
}

function* ensureRemovedBTGConsistency() {
  const activeBoundary: ExtendedBoundaryIdentifier | null = yield select(activeBoundarySelector);
  const boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield select(boundaryTerritoryGroupsSelector);

  if (activeBoundary?.boundaryTerritoryGroupId && boundaryTerritoryGroups.every(btg => btg.boundaryTerritoryGroupId !== activeBoundary?.boundaryTerritoryGroupId)) {
    yield put(activeMapElementsClearState());
  }
}

function* onBoundaryTerritoryGroupsBulkSave(
  action: PickAction<BoundaryTerritoryGroupsAction, typeof boundaryTerritoryGroupsBulkSaveRequest.type>,
) {
  const { boundaryTerritoryGroups, onSuccess, onError } = action.payload;
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isMapOffline: boolean = yield select<boolean>(isMapInOfflineModeSelector);

  if (!clientId || !notEmpty(boundaryTerritoryGroups)) {
    yield put(boundaryTerritoryGroupsUpdateError());
    onError?.();
    return;
  }

  const offlineUpdateBTGs = isMapOffline ? boundaryTerritoryGroups : boundaryTerritoryGroups.filter(btg => isArtificialBoundaryTerritoryGroup(btg));
  const onlineUpdateBTGs = isMapOffline ? [] : boundaryTerritoryGroups.filter(btg => !isArtificialBoundaryTerritoryGroup(btg));

  try {
    if (notEmpty(offlineUpdateBTGs)) {
      yield call(
        updateOfflineBoundaryTerritoryGroups,
        offlineUpdateBTGs,
        {
          fetchBoundaryDetails: true,
          failSilently: false,
        },
        clientId,
      );
    }

    if (notEmpty(onlineUpdateBTGs)) {
      const updateRequests = onlineUpdateBTGs.map(createBoundaryTerritoryUpdateRequestFromBoundaryTerritoryGroup);
      const updatedBTGs: Awaited<ReturnType<typeof bulkUpdateBoundaryTerritoryGroups>> = yield call(bulkUpdateBoundaryTerritoryGroups, clientId, updateRequests);
      yield put(boundaryTerritoryGroupUpdateSuccess(updatedBTGs.data, { fetchBoundaryDetails: true }));
    }

    onSuccess?.();
  }
  catch (error) {
    console.error('Failed to save boundary territory groups:', error);
    const collisionBtgId = Object.keys(error.response?.reason ?? {})[0];
    const collisionBtg = boundaryTerritoryGroups.find(btg => btg.boundaryTerritoryGroupId === Number(collisionBtgId));
    yield call(onBoundaryTerritoryGroupUpdateError, {
      error,
      boundaryTerritoryGroup: collisionBtg ?? boundaryTerritoryGroups[0],
      onSuccess,
      failSilently: false,
      refetchOnConcurrencyError: false,
    } satisfies Parameters<typeof onBoundaryTerritoryGroupUpdateError>[0]);
    onError?.();
  }
}

export function* onBoundaryTerritoryGroupUpdateError({ error, boundaryTerritoryGroup, onSuccess, failSilently, refetchOnConcurrencyError }: {
  error: any;
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  onSuccess?: () => void;
  failSilently: boolean;
  refetchOnConcurrencyError: boolean;
}) {
  const clientId: number | null = yield select(clientIdSelector);
  // Fill was potentially already added to the map
  if (isBoundaryTerritoryCreateUpdateApiError(error) && (clientId !== null)) {
    const boundaryTerritoryGroups: ReturnType<typeof boundaryTerritoryGroupsSelector> =
      yield select<ReturnType<typeof boundaryTerritoryGroupsSelector>>(boundaryTerritoryGroupsSelector);
    const btgToSelect = boundaryTerritoryGroups.find(btg => btg.boundaryTerritoryGroupId === error.response.btg);

    if (btgToSelect) {
      onSuccess?.();
      yield put(boundaryTerritoryGroupsUpdateCancel());
      yield put(boundaryTerritoryGroupHighlightStarted(btgToSelect.boundaryGroupId));
      yield put(boundaryTerritoryGroupActivate(btgToSelect));

      return;
    }
    else if (refetchOnConcurrencyError) {
      const mapId: number | null = yield select<number | null>(mapIdSelector);
      if (mapId === null) {
        return;
      }
      yield put(boundaryTerritoryGroupsFetchRequest(mapId));
      return;
    }
    else {
      yield call(openConcurrencyErrorModal, {
        boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
      });
    }
  }
  yield put(boundaryTerritoryGroupsUpdateError());

  if (failSilently) {
    return;
  }

  const isBoundaryTerritoryGroupRemoved: boolean = yield handleTerritoryGroupAlreadyRemoved(error, boundaryTerritoryGroup);
  if (isBoundaryTerritoryGroupRemoved) {
    const isGroupCustom: ReturnType<typeof boundaryGroupIsCustomSelector> = yield select(boundaryGroupIsCustomSelector, boundaryTerritoryGroup.boundaryGroupId);
    onSuccess?.();

    yield put(createAppError({
      type: AppErrorType.General,
      title: translate(isGroupCustom ? 'Territory group was already removed' : 'Boundary was already removed'),
      errorTitle: translate(isGroupCustom ? 'Territory group was already removed from the map' : 'Boundary was already removed from the map'),
    }));
  }
  else if (isApiConcurrencyError(error)) {
    if (refetchOnConcurrencyError) {
      const mapId: number | null = yield select<number | null>(mapIdSelector);
      if (mapId === null) {
        return;
      }
      yield put(boundaryTerritoryGroupsFetchRequest(mapId));
      return;
    }

    yield call(openConcurrencyErrorModal, {
      boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
      userName: error.response?.reason?.user_name,
    });
  }
  else {
    yield put(createAppError({
      type: AppErrorType.General,
      errorTitle: translate('An error occurred while updating the data'),
      title: translate('Boundary Update Error'),
    }));
  }
}

function* openConcurrencyErrorModal({
  boundaryGroupId,
  userName,
}: {
  boundaryGroupId: BoundaryTerritoryGroupId;
  userName?: any;
}) {
  const modalId = createUuid();

  const onClose = () => {
    appStore.dispatch(closeModal(modalId));
  };

  const isGroupCustom: ReturnType<typeof boundaryGroupIsCustomSelector> = yield select(boundaryGroupIsCustomSelector,
    boundaryGroupId);

  yield put(openNewModalWithId(modalId, 'Confirmation', {
    isConfirmButtonDestructive: true,
    title: translate(isGroupCustom ? 'Territories Out Of Sync' : 'Boundaries Out Of Sync'),
    children: translate(isGroupCustom ?
      'Territories have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.' :
      'Boundaries have been recently changed by {{userName}}. Please refresh your data in order to be able to make changes.',
    {
      userName: userName ?? translate('another user'),
    }),
    confirmCaption: translate('Refresh Page'),
    onCancel: () => {
      onClose();
    },
    onConfirm: () => {
      refreshPage();
    },
  }));
}
